import { useApolloClient, useQuery } from "@apollo/client";
import { NOTIFICATION_SUBSCRIPTION } from "../../components/layout/header/notifications/subscriptions";
import React from "react";
import { FormattedMessage } from "react-intl";
import { ListNotification } from "../../components/notifications/ListNotification";
import { PrimarySpinner } from "../../components/spinner/primary-spinner";
import {
  Box,
  Card,
  Typography,
  Skeleton,
  Alert,
  Fade,
  useTheme,
  useMediaQuery,
  List,
} from "@mui/material";
import {
  GET_NOTIFICATIONS,
  GET_COURSE_TEACHINGS,
} from "../../apollo-client/queries";
import { NOTIFICATION_TYPES } from "../../notification-constants";

export const HomePageNotificationsBox = () => {
  const client = useApolloClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    data: dataNotifications,
    loading: loadingNotifications,
    error: errorNotifications,
    subscribeToMore: subscribeToMoreNotifications,
  } = useQuery(GET_NOTIFICATIONS, {
    variables: { limit: 4 },
  });

  subscribeToMoreNotifications({
    document: NOTIFICATION_SUBSCRIPTION,
    updateQuery: async (prev, { subscriptionData }) => {
      if (!subscriptionData.data) return prev;

      const newNotification = subscriptionData.data.newNotification;

      if (
        newNotification.notificationType === NOTIFICATION_TYPES.NEW_TEACHING
      ) {
        await client.refetchQueries({ include: [GET_COURSE_TEACHINGS] });
      }

      return Object.assign({}, prev, {
        user: {
          ...prev.user,
          notifications: [newNotification, ...prev.user.notifications],
        },
      });
    },
  });

  const notifications = dataNotifications?.user?.notifications || [];
  const hasNotifications = notifications.length > 0;

  return (
    <Card
      elevation={2}
      sx={{
        width: '100%',
        maxWidth: 600,
        mx: 'auto',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        ...(isMobile && {
          mx: 0,
          borderRadius: 2,
        }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FormattedMessage id="abo-notifications.notifications" />
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ position: 'relative', minHeight: 200 }}>
        {/* Error State */}
        {errorNotifications && (
          <Alert 
            severity="error" 
            sx={{ m: 2 }}
            onClose={() => window.location.reload()}
          >
            <FormattedMessage 
              id="notifications.error.loading" 
              defaultMessage="Failed to load notifications. Please try again."
            />
          </Alert>
        )}

        {/* Loading State */}
        {loadingNotifications && !hasNotifications && (
          <Box sx={{ p: 2 }}>
            {[...Array(3)].map((_, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Skeleton variant="circular" width={48} height={48} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="text" width="60%" height={16} sx={{ mt: 0.5 }} />
                    <Skeleton variant="text" width="40%" height={14} sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Notifications List */}
        {!errorNotifications && hasNotifications && (
          <Fade in={hasNotifications} timeout={300}>
            <List sx={{ p: 0 }}>
              {notifications.map((notification, i, notificationsArray) => (
                <ListNotification
                  key={`notification_${i}`}
                  notification={notification}
                  prevNotification={notificationsArray[i - 1]}
                  setIsNotificationsMenuOpen={() => {}} // Always open on homepage
                  isLast={i === notificationsArray.length - 1}
                />
              ))}
            </List>
          </Fade>
        )}

        {/* Empty State */}
        {!errorNotifications && !loadingNotifications && !hasNotifications && (
          <Fade in={!hasNotifications} timeout={300}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center',
                minHeight: 200,
              }}
            >
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  maxWidth: 300,
                  lineHeight: 1.6,
                }}
              >
                Abonniere Module komplett, oder Teilbereiche, oder stelle Fragen und
                lass sie beantworten
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Loading More Indicator */}
        {loadingNotifications && hasNotifications && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <PrimarySpinner />
          </Box>
        )}
      </Box>
    </Card>
  );
};

